/* ZakelijkWachtwoordPage - Eerste keer inloggen: wachtwoord wijzigen.
   Updated: Initial creation - zakelijke klant stelt nieuw wachtwoord in. */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader as Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

export default function ZakelijkWachtwoordPage() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens lang zijn');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/zakelijk/inloggen');
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      // Clear must_reset_password flag
      await supabase
        .from('business_customers')
        .update({ must_reset_password: false })
        .eq('auth_user_id', user.id);

      setDone(true);
      toast.success('Wachtwoord gewijzigd!');
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#fcfaf6] flex items-center justify-center p-4">
        <Helmet>
          <title>Wachtwoord gewijzigd | Buitn Planten Zakelijk</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-[#e7e1d6] p-10 text-center"
        >
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1e2b22] mb-3">Wachtwoord gewijzigd!</h2>
          <p className="text-[#5a6552] mb-6">Je wachtwoord is succesvol gewijzigd. Je wordt doorgestuurd naar het zakelijk portaal.</p>
          <Button
            onClick={() => navigate('/zakelijk')}
            className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
          >
            Naar zakelijk portaal
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaf6] flex items-center justify-center p-4">
      <Helmet>
        <title>Wachtwoord wijzigen | Buitn Planten Zakelijk</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-6">
          <img
            src="/assets/logos/buitnplanten-logo-green.webp"
            alt="Buitn Planten"
            className="h-16 mx-auto mb-3"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <p className="text-sm font-medium text-[#5a6552] tracking-wide uppercase">Zakelijk Portaal</p>
        </div>

        <div className="bg-white/85 backdrop-blur-xl rounded-3xl shadow-xl border border-[#e7e1d6] p-8">
          <h1 className="text-xl font-bold text-[#1e2b22] mb-2">Wachtwoord wijzigen</h1>
          <p className="text-sm text-[#5a6552] mb-6">
            Dit is de eerste keer dat je inlogt. Stel een nieuw wachtwoord in om je account te beveiligen.
          </p>

          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm font-medium text-[#1e2b22]">
                Nieuw wachtwoord
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimaal 8 tekens"
                  required
                  disabled={loading}
                  className="pl-10 h-12 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-medium text-[#1e2b22]">
                Herhaal nieuw wachtwoord
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="pl-10 h-12 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1e2b22] hover:bg-[#2a3a2f] text-white font-semibold shadow-lg shadow-[#1e2b22]/20 transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Opslaan...
                </>
              ) : (
                'Wachtwoord opslaan'
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
